import dayjs from 'dayjs';
import { reconcile, type PosSnapshot } from '../Treatment/posSync';
import { TREATMENT_SERVICES, type TreatmentCard, type TreatmentConfig, type TreatmentPackage } from '../Treatment/data';
import { cardBalance, cardStatus, consumedAmount, installmentRows, lineBalances, outstanding, paidAmount, usageItems } from '../Treatment/logic';
import {
  billTotal, depositsOf, fmtMoney, invoiceLines, isSale, paidTotal, rateOn, splitVat, ymd,
  type Bill, type CommissionRate, type KtState, type RefundCase, type Team, type TeamRole, type UsageEntry,
} from './ktLogic';

/**
 * Kế toán nội bộ bán hàng (SRS 2.1 đến 2.5): thẻ liệu trình đọc từ module Liệu trình và đơn POS, nhật ký trừ thẻ số riêng,
 * chốt chặn công nợ, duyệt hoàn tiền, hoa hồng và tua. Toàn bộ là hàm thuần.
 */

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const BRANCH = 'q1';

export const usageNo = (seq: number) => `TLT-${String(seq).padStart(5, '0')}`;
export const customerCode = (phone: string) => { const d = phone.replace(/\D/g, ''); return d ? `KH${d.slice(-6)}` : 'Chưa có mã'; };

// ===================== Bóc tách chiết khấu =====================

/** Chia chiết khấu tổng đơn về từng dòng theo giá trị sau chiết khấu dòng; dòng cuối nhận phần lẻ để khớp tổng */
export function allocateDiscount(amounts: number[], discount: number): number[] {
  const total = sum(amounts);
  if (!total || !discount) return amounts.map(() => 0);
  let used = 0;
  return amounts.map((a, i) => {
    if (i === amounts.length - 1) return discount - used;
    const part = Math.round((discount * a) / total);
    used += part;
    return part;
  });
}

/** Doanh thu net từng dịch vụ trong kỳ, đã bỏ VAT: gói tính khi làm từng buổi, không tính lúc bán thẻ */
export function netByService(s: Pick<KtState, 'bills' | 'cards' | 'usageLog' | 'rates' | 'services'>, ym: string) {
  const map = new Map<string, { name: string; gross: number; net: number; discount: number; sessions: number }>();
  const row = (name: string) => { if (!map.has(name)) map.set(name, { name, gross: 0, net: 0, discount: 0, sessions: 0 }); return map.get(name)!; };
  s.bills.filter(b => b.status === 'valid' && b.kind === 'service' && b.date.startsWith(ym)).forEach(b => invoiceLines(s, b).forEach(l => {
    const r = row(l.name);
    r.gross += l.gross; r.net += splitVat(l.gross, rateOn(s.rates, l.serviceId, b.date)?.rate ?? null).net;
    r.discount += (l.lineDiscount || 0) + (l.orderDiscount || 0);
  }));
  s.usageLog.filter(e => !e.cancelled && e.date.startsWith(ym)).forEach(e => {
    const card = s.cards.find(c => c.id === e.cardId);
    if (!card) return;
    usageLines(s, card, e).forEach(l => { const r = row(l.name); r.gross += l.gross; r.net += l.net; r.sessions += l.qty; });
  });
  return Array.from(map.values()).sort((a, b) => b.net - a.net);
}

// ===================== Thẻ: bill gốc, giá trị buổi =====================

export const billOfCard = (s: Pick<KtState, 'bills'>, c: TreatmentCard): Bill | null => (c.orderId ? s.bills.find(b => b.posOrderId === c.orderId || b.id === c.orderId) || null : null);
const serviceCode = (id: string) => TREATMENT_SERVICES.find(x => x.id === id)?.code || id;
const saleDate = (c: TreatmentCard) => ymd(new Date(c.createdAt).getTime());
/** VAT đã kê lúc bán thẻ nên kết chuyển dùng thuế suất tại ngày bán */
const lineRate = (s: Pick<KtState, 'rates'>, c: TreatmentCard, serviceId: string) => rateOn(s.rates, serviceCode(serviceId), saleDate(c))?.rate ?? null;

/** Thẻ giá trị trừ theo giá dịch vụ, nhưng tiền thật chỉ bằng tỷ lệ giá bán trên mệnh giá */
const realValue = (c: TreatmentCard, value: number) => (c.kind === 'value' && c.faceValue > 0 ? Math.round((value * c.price) / c.faceValue) : value);

/** Bóc tách giá trị thực tế của một lượt trừ thẻ: truy về giá sau chiết khấu trên bill gốc, bỏ VAT */
export function usageLines(s: Pick<KtState, 'rates'>, c: TreatmentCard, e: Pick<UsageEntry, 'items'>) {
  return e.items.map(i => {
    const gross = realValue(c, i.value);
    const rate = lineRate(s, c, i.serviceId);
    const v = splitVat(gross, rate);
    const line = c.lines.find(l => l.serviceId === i.serviceId);
    return { serviceId: i.serviceId, name: i.name, qty: i.qty, unit: line?.unitValue ?? 0, gross, net: v.net, vat: v.vat, rate, bonus: line?.role === 'bonus', group: line?.group || TREATMENT_SERVICES.find(x => x.id === i.serviceId)?.group || '' };
  });
}

/** Phần chưa VAT của một số tiền trên thẻ, theo thuế suất tại ngày bán */
export const netOfCard = (s: Pick<KtState, 'rates'>, c: TreatmentCard, gross: number) => splitVat(gross, lineRate(s, c, c.lines[0]?.serviceId || '')).net;

export function traceUsage(s: Pick<KtState, 'bills' | 'cards' | 'rates'>, e: UsageEntry) {
  const card = s.cards.find(c => c.id === e.cardId);
  if (!card) return null;
  const bill = billOfCard(s, card);
  const lines = usageLines(s, card, e);
  return { card, bill, lines, net: sum(lines.map(l => l.net)), gross: sum(lines.map(l => l.gross)), origin: bill ? `Bill ${bill.no}` : 'Chuyển từ MySpa, không có bill trên hệ thống' };
}

export interface CardRow {
  card: TreatmentCard; bill: Bill | null; status: ReturnType<typeof cardStatus>; used: number; total: number; left: number;
  /** Giá trị còn trên TK 3387, chưa VAT */
  remainingNet: number; debt: number; daysToExpire: number; warn: 'expired' | 'expiring' | null;
}

/** Bảng thẻ cho kế toán: buổi, hạn, nợ, phần còn nằm trên TK 3387 */
export function cardRows(s: Pick<KtState, 'bills' | 'cards' | 'rates'>, today: string, expiringDays = 30): CardRow[] {
  return s.cards.map(card => {
    const b = cardBalance(card);
    const status = cardStatus(card, dayjs(today).toDate());
    const live = status === 'active' || status === 'expired';
    const remaining = live ? Math.max(0, card.price - consumedAmount(card)) : 0;
    const rate = lineRate(s, card, card.lines[0]?.serviceId || '');
    const daysToExpire = dayjs(card.expiresAt).startOf('day').diff(dayjs(today).startOf('day'), 'day');
    return {
      card, bill: billOfCard(s, card), status,
      used: b.usedSessions, total: b.totalSessions, left: b.remainingSessions,
      remainingNet: splitVat(Math.round(remaining), rate).net, debt: outstanding(card), daysToExpire,
      warn: status === 'expired' ? 'expired' : status === 'active' && daysToExpire <= expiringDays ? 'expiring' : null,
    };
  });
}

/** Máy công nghệ cao: dịch vụ nhóm này bắt buộc chọn máy khi ghi ca (SRS 3.3). Danh sách demo, bản thật lấy từ danh mục tài sản */
export const KT_MACHINES: { code: string; name: string; group: string }[] = [
  { code: 'MAY-THERMAGE-01', name: 'Thermage FLX', group: 'Trẻ hóa da' },
  { code: 'MAY-HIFU-01', name: 'Hifu Ultraformer', group: 'Nâng cơ' },
  { code: 'MAY-PICO-01', name: 'Laser Pico phòng 1', group: 'Laser' },
  { code: 'MAY-PICO-02', name: 'Laser Pico phòng 2', group: 'Laser' },
];
export const MACHINES: Record<string, string> = { 'Trẻ hóa da': 'MAY-THERMAGE-01', 'Nâng cơ': 'MAY-HIFU-01', Laser: 'MAY-PICO-01' };
export const machineFor = (c: TreatmentCard, serviceId: string) => MACHINES[c.lines.find(l => l.serviceId === serviceId)?.group || ''] || '';

// ===================== Chốt chặn cứng =====================

/** Khách còn nợ thì không tạo ca, không trừ buổi; không có lối duyệt vượt */
export function usageBlock(s: Pick<KtState, 'cards' | 'refundCases' | 'cardGate' | 'locks'>, cardId: string, serviceId: string, qty: number, today: string): string[] {
  const c = s.cards.find(x => x.id === cardId);
  if (!c) return ['Không tìm thấy thẻ'];
  const errors: string[] = [];
  const status = cardStatus(c, dayjs(today).toDate());
  if (status === 'refunded' || status === 'cancelled') errors.push(status === 'refunded' ? 'Thẻ đã hoàn tiền' : 'Thẻ đã hủy');
  if (status === 'completed') errors.push('Thẻ đã dùng hết');
  if (status === 'expired') errors.push('Thẻ đã quá hạn: Kế toán trưởng xử lý gia hạn trước');
  if (s.locks[today]) errors.push('Sổ hôm nay đã khóa, không ghi thêm lượt trừ thẻ');
  if (s.refundCases.some(r => r.cardId === cardId && ['pending_l1', 'pending_l2', 'approved'].includes(r.status))) errors.push('Thẻ đang có hồ sơ hoàn tiền');
  const line = lineBalances(c).find(l => l.serviceId === serviceId);
  if (c.kind === 'session' && !line) errors.push('Dịch vụ không có trong thẻ');
  if (line && qty > line.available) errors.push(`${line.name} chỉ còn ${Math.max(0, line.available)} buổi`);
  const debt = outstanding(c);
  if (s.cardGate === 'full' && debt > 0) errors.push(`Thẻ còn nợ ${fmtMoney(debt)}: thu đủ tiền trước khi trừ buổi`);
  if (s.cardGate === 'paid') {
    const overdue = installmentRows(c, { dueSoonDays: 0 }, dayjs(today).toDate()).filter(r => r.state === 'overdue');
    if (overdue.length) errors.push(`Còn ${fmtMoney(sum(overdue.map(r => r.left)))} quá hạn thanh toán`);
    // Thẻ giá trị không có dòng dịch vụ: lượt này trừ theo giá dịch vụ, quy về tiền thật như consumedAmount
    const use = c.kind === 'value' ? realValue(c, sum(usageItems(c, [{ serviceId, qty }]).map(i => i.value))) : (line?.unitValue || 0) * qty;
    const after = consumedAmount(c) + use;
    if (after > paidAmount(c) + 1000) errors.push(`Giá trị đã dùng sau lượt này ${fmtMoney(after)} vượt số đã đóng ${fmtMoney(paidAmount(c))}`);
  }
  return errors;
}

/** Đơn dịch vụ lẻ còn nợ thì không tạo ca: để Lễ tân, POS gọi trước khi điều phối */
export const orderBlock = (b: Pick<Bill, 'no' | 'debt' | 'status'>): string | null => (b.status === 'valid' && b.debt > 0 ? `${b.no} còn nợ ${fmtMoney(b.debt)}: thu đủ tiền trước khi tạo ca` : null);

export const paidUpTo = (c: TreatmentCard, at: string) => sum(c.payments.filter(p => p.at <= at).map(p => p.amount));

// ===================== Đồng bộ thẻ và nhật ký trừ thẻ =====================

export interface CardSource { treatment: TreatmentCard[]; packages: TreatmentPackage[]; cfg: TreatmentConfig; pos: PosSnapshot }

/** Lượt trừ mới vào nhật ký với số kế tiếp; không chèn lùi vào ngày đã khóa */
export function journal(s0: KtState, now: number): KtState {
  let s = s0;
  const alert = (cardId: string, text: string) => {
    const id = `${cardId}:${text}`;
    if (!s.posAlerts.some(a => a.id === id)) s = { ...s, posAlerts: [{ id, at: now, orderId: cardId, text }, ...s.posAlerts] };
  };
  const usages = s.cards.flatMap(c => c.usages.map(u => ({ c, u }))).sort((a, b) => a.u.at.localeCompare(b.u.at));
  usages.forEach(({ c, u }) => {
    const found = s.usageLog.find(e => e.id === u.id);
    const date = ymd(new Date(u.at).getTime());
    if (found) {
      if (!u.cancelled || found.cancelled) return;
      if (s.locks[found.date]) { alert(c.id, `Lượt ${found.no} bị hủy bên Liệu trình sau khi sổ ngày ${found.date} đã khóa. Lượt giữ nguyên, cần Kế toán trưởng xử lý.`); return; }
      s = { ...s, usageLog: s.usageLog.map(e => (e.id === u.id ? { ...e, cancelled: u.cancelled!.reason } : e)) };
      return;
    }
    if (s.locks[date]) { alert(c.id, `Lượt trừ thẻ ${c.packageName} của ${c.customerName} ngày ${date} chưa vào nhật ký vì ngày đó đã khóa.`); return; }
    const seq = Math.max(0, ...s.usageLog.map(e => e.seq)) + 1;
    const staff = u.by && u.staffId ? { ktv: u.by } : {};
    const entry: UsageEntry = {
      id: u.id, seq, no: usageNo(seq), cardId: c.id, date, at: new Date(u.at).getTime(), items: u.items, team: staff, machine: '',
      cancelled: u.cancelled?.reason, debtAtUse: paidUpTo(c, u.at) < c.price, overrideBy: u.overrideBy,
    };
    s = { ...s, usageLog: [...s.usageLog, entry] };
  });
  return s;
}

/** Đọc thẻ từ Liệu trình và đơn POS: chỉ đọc; chạy lại không sinh trùng */
export function syncCards(s0: KtState, src: CardSource, now: number): KtState {
  let cards = s0.cards;
  const refunded = (id: string) => s0.refundCases.some(r => r.cardId === id && r.status === 'paid');
  src.treatment.filter(t => t.branchId === BRANCH).forEach(t => {
    const next = refunded(t.id) ? { ...t, status: 'refunded' as const } : t;
    const same = cards.find(c => c.id === t.id);
    if (same) { if (JSON.stringify(same) !== JSON.stringify(next)) cards = cards.map(c => (c.id === t.id ? next : c)); return; }
    // Liệu trình đã phát hành thẻ cho dòng gói mà sổ kế toán đã tự dựng: lấy bản của Liệu trình
    const twin = t.orderId ? cards.find(c => c.orderId === t.orderId && c.lineKey === t.lineKey) : undefined;
    if (twin && twin.usages.length) return;
    cards = twin ? cards.map(c => (c.id === twin.id ? next : c)) : [...cards, next];
  });
  let n = cards.length;
  const nextId = (kind: string) => { n += 1; return `kt-${kind}-${now.toString(36)}-${n}`; };
  const orders = src.pos.orders.filter(o => o.branchId === BRANCH);
  const r = reconcile(cards, { ...src.pos, orders }, { packages: src.packages, cfg: src.cfg, since: '2000', nextId });
  if (r.cards) cards = r.cards;
  const s = cards === s0.cards ? s0 : { ...s0, cards };
  return journal(s, now);
}

// ===================== Cọc và công nợ theo Mã KH =====================

export type DepositState = 'used' | 'active' | 'expiring' | 'overdue';
export const DEPOSIT_STATE: Record<DepositState, string> = { used: 'Đã dùng', active: 'Còn hạn', expiring: 'Sắp hết hạn', overdue: 'Quá hạn' };
const DEPOSIT_WARN_DAYS = 7;

export const depositUntil = (s: Pick<KtState, 'depositDays'>, b: Bill) => b.depositUntil || b.program?.until || dayjs(b.date).add(s.depositDays, 'day').format('YYYY-MM-DD');

export function depositInfo(s: Pick<KtState, 'bills' | 'depositDays'>, b: Bill, today: string) {
  const until = depositUntil(s, b);
  const used = s.bills.find(x => x.depositOf === b.id && x.status === 'valid');
  const days = dayjs(until).startOf('day').diff(dayjs(today).startOf('day'), 'day');
  const state: DepositState = used ? 'used' : days < 0 ? 'overdue' : days <= DEPOSIT_WARN_DAYS ? 'expiring' : 'active';
  return { until, days, state, usedBy: used?.no || '' };
}

export function depositRows(s: Pick<KtState, 'bills' | 'depositDays'>, today: string) {
  return depositsOf(s).map(b => ({ bill: b, code: customerCode(b.phone), amount: billTotal(b), ...depositInfo(s, b, today) }));
}

/** Báo cáo nhắc cọc quá hạn để xuất Excel */
export const overdueDepositSheet = (s: Pick<KtState, 'bills' | 'depositDays'>, today: string) => depositRows(s, today).filter(r => r.state === 'overdue').map(r => ({
  'Mã KH': r.code, 'Khách hàng': r.bill.customer, 'Điện thoại': r.bill.phone, 'Số bill cọc': r.bill.no, 'Ngày cọc': r.bill.date, 'Chương trình': r.bill.program?.name || '',
  'Hạn cọc': r.until, 'Quá hạn (ngày)': -r.days, 'Số tiền cọc': r.amount,
}));

export interface DebtRow { code: string; customer: string; phone: string; deposit: number; overdueDeposit: number; billDebt: number; cardDebt: number; locked: boolean }

/** Công nợ gộp theo Mã KH: cọc đang giữ, nợ đơn, nợ thẻ chuyển từ MySpa (thẻ có bill đã tính ở nợ đơn) */
export function debtRows(s: Pick<KtState, 'bills' | 'cards' | 'depositDays'>, today: string): DebtRow[] {
  const map = new Map<string, DebtRow>();
  const row = (phone: string, customer: string) => {
    const code = customerCode(phone);
    const key = phone.replace(/\D/g, '') || customer;
    if (!map.has(key)) map.set(key, { code, customer, phone, deposit: 0, overdueDeposit: 0, billDebt: 0, cardDebt: 0, locked: false });
    return map.get(key)!;
  };
  depositRows(s, today).filter(r => r.bill.status === 'valid' && r.state !== 'used').forEach(r => {
    const d = row(r.bill.phone, r.bill.customer);
    d.deposit += r.amount;
    if (r.state === 'overdue') d.overdueDeposit += r.amount;
  });
  s.bills.filter(b => b.status === 'valid' && b.debt > 0).forEach(b => { const d = row(b.phone, b.customer); d.billDebt += b.debt; d.locked = true; });
  s.cards.filter(c => !billOfCard(s, c) && outstanding(c) > 0).forEach(c => { const d = row(c.customerPhone, c.customerName); d.cardDebt += outstanding(c); d.locked = true; });
  return Array.from(map.values()).sort((a, b) => (b.billDebt + b.cardDebt + b.deposit) - (a.billDebt + a.cardDebt + a.deposit));
}

// ===================== Hoàn tiền, hủy thẻ =====================

export const CASE_STATUS: Record<RefundCase['status'], string> = {
  pending_l1: 'Chờ Kế toán nội bộ', pending_l2: 'Chờ Kế toán trưởng', approved: 'Đã duyệt, chờ chi', paid: 'Đã chi', rejected: 'Từ chối',
};

export interface CaseInput { penaltyPct: number; testFee: number }

/**
 * Tiền hoàn nét = đã đóng − đã dùng − phạt hủy − phí xét nghiệm − phí cà thẻ.
 * Phí cà thẻ lấy số thực trong file Payoo nếu đã đối soát, chưa có thì theo tỷ lệ mặc định, chỉ tính trên phần được hoàn.
 */
export function refundQuoteOf(s: Pick<KtState, 'payoo' | 'posFeePct'>, c: TreatmentCard, input: CaseInput) {
  const paid = paidAmount(c);
  const consumed = Math.round(consumedAmount(c));
  const refundable = Math.max(0, paid - consumed);
  const penalty = Math.round((refundable * Math.max(0, input.penaltyPct)) / 100);
  const fees = sum(c.payments.filter(p => p.method === 'card').map(p => {
    const row = s.payoo.find(r => r.ref.toUpperCase() === p.txnRef.toUpperCase());
    return row ? row.fee : Math.round((p.amount * s.posFeePct) / 100);
  }));
  const posFee = paid ? Math.round((fees * refundable) / paid) : 0;
  const testFee = Math.min(Math.max(0, input.testFee), refundable);
  return { paid, consumed, refundable, penalty, testFee, posFee, net: Math.max(0, refundable - penalty - testFee - posFee) };
}

export function caseBlock(s: Pick<KtState, 'refundCases'>, c: TreatmentCard): string | null {
  if (c.status !== 'active') return 'Thẻ không còn hiệu lực';
  if (c.policy && !c.policy.allowRefund) return 'Gói này không cho hoàn tiền';
  if (outstanding(c) > 0) return `Thẻ còn nợ ${fmtMoney(outstanding(c))}: xử lý công nợ trước khi hủy`;
  if (paidAmount(c) <= consumedAmount(c)) return 'Tiền đã đóng đã dùng hết, không còn gì để hoàn';
  if (s.refundCases.some(r => r.cardId === c.id && r.status !== 'rejected')) return 'Thẻ đã có hồ sơ hoàn tiền';
  return null;
}

// ===================== Hoa hồng và tua =====================

export const TEAM_ROLE: Record<TeamRole, string> = { doctor: 'Bác sĩ chính', assistant: 'Phụ mổ', nurse: 'Điều dưỡng', ktv: 'KTV' };
export const TEAM_ROLES = Object.keys(TEAM_ROLE) as TeamRole[];
export const hasTeam = (t?: Team) => !!t && TEAM_ROLES.some(r => !!t[r]);

/** Mã nhân sự thực hiện. Danh sách demo, bản thật lấy từ hồ sơ nhân sự */
export const KT_STAFF: { code: string; name: string; role: TeamRole }[] = [
  { code: 'BS001', name: 'BS. Demo An', role: 'doctor' },
  { code: 'BS002', name: 'BS. Demo Khoa', role: 'doctor' },
  { code: 'PM001', name: 'Phụ mổ Demo Bình', role: 'assistant' },
  { code: 'DD001', name: 'ĐD. Demo Chi', role: 'nurse' },
  { code: 'KTV001', name: 'KTV Demo Dũng', role: 'ktv' },
  { code: 'KTV002', name: 'KTV Demo Hương', role: 'ktv' },
];
export const staffOptions = (role: TeamRole) => KT_STAFF.filter(x => x.role === role).map(x => ({ value: x.name, label: `${x.code} · ${x.name}` }));
export const staffLabel = (name: string) => { const x = KT_STAFF.find(y => y.name === name); return x ? `${x.code} · ${x.name}` : name; };
export const machineOptions = (groups: string[]) => KT_MACHINES.filter(m => groups.includes(m.group)).map(m => ({ value: m.code, label: `${m.code} · ${m.name}` }));

/** Bắt buộc có Bác sĩ, KTV hoặc Điều dưỡng, đúng mã đúng vai; nhóm có máy thì bắt buộc đúng mã máy của nhóm đó */
export function sessionErrors(team: Team, groups: string[], machine: string): string[] {
  const errors: string[] = [];
  if (!team.doctor && !team.ktv && !team.nurse) errors.push('Chọn Bác sĩ, KTV hoặc Điều dưỡng');
  TEAM_ROLES.forEach(r => { if (team[r] && !KT_STAFF.some(x => x.name === team[r] && x.role === r)) errors.push(`${TEAM_ROLE[r]}: ${team[r]} không có trong danh sách mã nhân sự`); });
  const allowed = KT_MACHINES.filter(m => groups.includes(m.group));
  if (allowed.length && !machine.trim()) errors.push('Dịch vụ này bắt buộc chọn mã máy');
  if (machine.trim() && !allowed.some(m => m.code === machine.trim())) errors.push(`Máy ${machine.trim()} không dùng cho dịch vụ này`);
  return errors;
}

export const rateOfGroup = (rates: CommissionRate[], group: string) => rates.find(r => r.group === group) || rates.find(r => r.group === '*')!;
const pct = (base: number, rate: number) => Math.round((base * rate) / 100);

type CommissionState = Pick<KtState, 'bills' | 'cards' | 'usageLog' | 'refundCases' | 'rates' | 'services' | 'commissionRates' | 'locks'>;

export interface CommissionRow { ym: string; role: 'sale' | 'cskh'; person: string; docNo: string; date: string; customer: string; desc: string; base: number; rate: number; amount: number; clawback?: boolean }
export interface TuaRow { ym: string; role: TeamRole; person: string; docNo: string; date: string; origin: string; desc: string; base: number; rate: number; amount: number; note?: string }

const ymOf = (date: string) => date.slice(0, 7);
const groupOf = (s: Pick<KtState, 'services'>, serviceId: string) => s.services.find(x => x.id === serviceId)?.group || '';

/** Hoa hồng Sale, CSKH: chỉ từ ngày đã duyệt khóa sổ, bỏ VAT trước khi nhân tỷ lệ; đơn còn nợ tính theo tiền đã thu; hoàn tiền thì truy thu */
export function commissionRows(s: CommissionState): CommissionRow[] {
  const rows: CommissionRow[] = [];
  const add = (role: 'sale' | 'cskh', person: string | undefined, b: Pick<CommissionRow, 'docNo' | 'date' | 'customer' | 'desc'>, base: number, group: string, clawback = false) => {
    const rate = rateOfGroup(s.commissionRates, group)[role];
    if (!person || !rate || !base) return;
    rows.push({ ...b, ym: ymOf(b.date), role, person, base: clawback ? -base : base, rate, amount: clawback ? -pct(base, rate) : pct(base, rate), clawback });
  };
  s.bills.filter(b => b.status === 'valid' && isSale(b) && s.locks[b.date]).forEach(b => {
    const lines = invoiceLines(s, b);
    const total = lines.reduce((t, l) => t + l.gross, 0);
    const received = paidTotal(b) + (total - billTotal(b));
    const ratio = total ? Math.min(1, received / total) : 0;
    lines.forEach(l => {
      const base = Math.round(splitVat(l.gross, rateOn(s.rates, l.serviceId, b.date)?.rate ?? null).net * ratio);
      const doc = { docNo: b.no, date: b.date, customer: b.customer, desc: l.name };
      add('sale', b.sale, doc, base, groupOf(s, l.serviceId));
      add('cskh', b.cskh, doc, base, groupOf(s, l.serviceId));
    });
    b.refunds.forEach(x => {
      const net = total ? Math.round((x.amount * lines.reduce((t, l) => t + splitVat(l.gross, rateOn(s.rates, l.serviceId, b.date)?.rate ?? null).net, 0)) / total) : 0;
      const doc = { docNo: b.no, date: x.date, customer: b.customer, desc: `Truy thu do hoàn tiền ${fmtMoney(x.amount)}` };
      const group = groupOf(s, lines[0]?.serviceId || '');
      if (s.refundCases.some(r => r.billId === b.id && r.status === 'paid')) return;
      add('sale', b.sale, doc, net, group, true);
      add('cskh', b.cskh, doc, net, group, true);
    });
  });
  s.refundCases.filter(r => r.status === 'paid' && r.paidDate).forEach(r => {
    const card = s.cards.find(c => c.id === r.cardId);
    if (!card) return;
    const bill = billOfCard(s, card);
    const rate = rateOn(s.rates, serviceCode(card.lines[0]?.serviceId || ''), saleDate(card))?.rate ?? null;
    const net = splitVat(r.net, rate).net;
    const doc = { docNo: r.no, date: r.paidDate!, customer: card.customerName, desc: `Truy thu do hoàn thẻ ${card.packageName}` };
    const group = card.lines[0]?.group || '';
    add('sale', bill?.sale || card.consultant, doc, net, group, true);
    add('cskh', bill?.cskh, doc, net, group, true);
  });
  return rows;
}

/** Bảng tua ekip: dịch vụ lẻ lấy theo dòng bill; buổi liệu trình truy về giá trị thực tế trên bill gốc */
export function tuaRows(s: CommissionState): TuaRow[] {
  const rows: TuaRow[] = [];
  const add = (team: Team | undefined, group: string, base: number, doc: Pick<TuaRow, 'docNo' | 'date' | 'origin' | 'desc' | 'note'>) => {
    TEAM_ROLES.forEach(role => {
      const person = team?.[role];
      const rate = rateOfGroup(s.commissionRates, group)[role];
      if (!person || !rate) return;
      rows.push({ ...doc, ym: ymOf(doc.date), role, person, base, rate, amount: pct(base, rate) });
    });
  };
  s.bills.filter(b => b.status === 'valid' && b.kind === 'service' && s.locks[b.date]).forEach(b => invoiceLines(s, b).forEach(l => {
    if (s.services.find(x => x.id === l.serviceId)?.type === 'product') return;
    const base = splitVat(l.gross, rateOn(s.rates, l.serviceId, b.date)?.rate ?? null).net;
    add(l.team || b.team, groupOf(s, l.serviceId), base, { docNo: b.no, date: b.date, origin: 'Dịch vụ lẻ', desc: `${l.name} · ${b.customer}` });
  }));
  s.usageLog.filter(e => !e.cancelled && s.locks[e.date]).forEach(e => {
    const t = traceUsage(s, e);
    if (!t) return;
    t.lines.forEach(l => add(e.team, l.group, l.net, {
      docNo: e.no, date: e.date, origin: t.origin, desc: `${l.name} ×${l.qty} · ${t.card.customerName}`,
      note: l.bonus ? 'Buổi tặng: giá trị 0, chờ Kế toán trưởng chốt cách tính tua' : undefined,
    }));
  });
  return rows;
}

export function summarize<T extends { person: string; role: string; amount: number; base: number }>(rows: T[]) {
  const map = new Map<string, { person: string; role: string; base: number; amount: number; count: number }>();
  rows.forEach(r => {
    const k = `${r.role}|${r.person}`;
    if (!map.has(k)) map.set(k, { person: r.person, role: r.role, base: 0, amount: 0, count: 0 });
    const x = map.get(k)!;
    x.base += r.base; x.amount += r.amount; x.count += 1;
  });
  return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
}

/** Chứng từ đã khóa nhưng chưa gán người: tiền hoa hồng, tua của các chứng từ này chưa được tính */
export function missingStaff(s: Pick<KtState, 'bills' | 'usageLog' | 'locks' | 'services'>) {
  const needsTeam = (b: Bill) => b.kind === 'service' && b.lines.some(l => s.services.find(x => x.id === l.serviceId)?.type !== 'product');
  const bills = s.bills.filter(b => b.status === 'valid' && isSale(b) && s.locks[b.date] && (!b.sale || !b.cskh || (needsTeam(b) && !hasTeam(b.team) && !b.lines.some(l => hasTeam(l.team)))));
  const usages = s.usageLog.filter(e => !e.cancelled && s.locks[e.date] && !hasTeam(e.team));
  return { bills, usages };
}
